import { configureStore } from '@reduxjs/toolkit';
import {
    persistStore,
    persistReducer,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
} from 'redux-persist';
const createNoopStorage = () => {
    return {
        getItem(_key: any) {
            return Promise.resolve(null);
        },
        setItem(_key: any, value: any) {
            return Promise.resolve(value);
        },
        removeItem(_key: any) {
            return Promise.resolve();
        },
    };
};

const storage =
    typeof window !== 'undefined'
        ? require('redux-persist/lib/storage').default
        : createNoopStorage();
import authReducer from './slices/authSlice';
import createDeckReducer from './slices/createDeckSlice';
import capTableReducer from './slices/capTableSlice';
import onboardingReducer from './slices/onboardingSlice';
import currencyReducer from './slices/currencySlice';
import { authApi } from './api/authApi';
import { pitchDeckApi } from './api/pitchDeckApi';
import { userApi } from './api/userApi';
import { documentBuilderApi } from './api/documentBuilderApi';
import { capTableApi } from './api/capTableApi';
import { onboardingApi } from './api/onboardingApi';
import { feedbackApi } from './api/feedbackApi';
import { chatbotApi } from './api/chatbotApi';
import { templateApi } from './api/templateApi';
import { pricingApi } from './api/pricingApi';
import { cartApi } from './api/cartApi';
import { contactApi } from './api/contactApi';
import { knowledgeBaseApi } from './api/knowledgeBaseApi';

const authPersistConfig = {
    key: 'auth',
    storage,
    whitelist: ['token', 'user', 'isAuthenticated'],
};

const currencyPersistConfig = {
    key: 'currency',
    storage,
    blacklist: ['locationFetched'],
};

const deckPersistConfig = {
    key: 'createDeck',
    storage,
    blacklist: ['researchReport'],
};

const capTablePersistConfig = {
    key: 'capTable',
    storage,
};

const onboardingPersistConfig = {
    key: 'onboarding',
    storage,
};

const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedCreateDeckReducer = persistReducer(deckPersistConfig, createDeckReducer);
const persistedCapTableReducer = persistReducer(capTablePersistConfig, capTableReducer);
const persistedOnboardingReducer = persistReducer(onboardingPersistConfig, onboardingReducer);
const persistedCurrencyReducer = persistReducer(currencyPersistConfig, currencyReducer);


export const store = configureStore({
    reducer: {
        auth: persistedAuthReducer,
        createDeck: persistedCreateDeckReducer,
        capTable: persistedCapTableReducer,
        onboarding: persistedOnboardingReducer,
        currency: persistedCurrencyReducer,
        [authApi.reducerPath]: authApi.reducer,
        [pitchDeckApi.reducerPath]: pitchDeckApi.reducer,
        [userApi.reducerPath]: userApi.reducer,
        [documentBuilderApi.reducerPath]: documentBuilderApi.reducer,
        [capTableApi.reducerPath]: capTableApi.reducer,
        [onboardingApi.reducerPath]: onboardingApi.reducer,
        [feedbackApi.reducerPath]: feedbackApi.reducer,
        [chatbotApi.reducerPath]: chatbotApi.reducer,
        [templateApi.reducerPath]: templateApi.reducer,
        [pricingApi.reducerPath]: pricingApi.reducer,
        [cartApi.reducerPath]: cartApi.reducer,
        [contactApi.reducerPath]: contactApi.reducer,
        [knowledgeBaseApi.reducerPath]: knowledgeBaseApi.reducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        }).concat(authApi.middleware, pitchDeckApi.middleware, userApi.middleware, documentBuilderApi.middleware, capTableApi.middleware, onboardingApi.middleware, feedbackApi.middleware, chatbotApi.middleware, templateApi.middleware, pricingApi.middleware, cartApi.middleware, contactApi.middleware, knowledgeBaseApi.middleware),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
